import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Modal from './MyModal'
import { AlertCircle, ArrowLeft, Info } from 'lucide-react'
import MyButton from './buttons'
import { Link } from 'react-router-dom'
import MyCheckbox from './MyCheckbox'
import CookieManager from '../lib/CookiesManager'
import { COOKIE_DESCRIPTIONS } from '../lib/cookieDescription'
import { FaBan } from 'react-icons/fa'
import { VscCircleSlash, VscPass } from 'react-icons/vsc'
import SentryManager from '../lib/SentryManager'

interface AgeRestrictionModalProps {
  isOpen: boolean
  onClose: () => void
}

const AgeRestrictionModal: React.FC<AgeRestrictionModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [isAgeRestrictionActive, setIsAgeRestrictionActive] = useState(
    !CookieManager.isAgeVerified(),
  )
  const [isInCookieSettings, setIsInCookieSettings] = useState(false)
  const [isEssentialCookiesEnabled, setIsEssentialCookiesEnabled] = useState(
    () => {
      const consent = CookieManager.getConsent()
      return consent.functional
    },
  )
  const [isAgeCookiesEnabled, setIsAgeCookiesEnabled] = useState(
    CookieManager.isAgeVerified(),
  )
  const [isAnalyticalCookiesEnabled, setIsAnalyticalCookiesEnabled] = useState(
    () => {
      const consent = CookieManager.getConsent()
      return consent.analytics
    },
  )
  const [showEssentialCookiesError, setShowEssentialCookiesError] =
    useState(false)
  const [showAgeCookiesError, setShowAgeCookiesError] = useState(false)
  const [openInfo, setOpenInfo] = useState<string | null>(null)

  const shouldHideModal =
    location.pathname.includes('/terms-of-use') ||
    location.pathname.includes('/cookies')

  if (shouldHideModal) {
    return null
  }

  const handleOpenCookieSettings = () => {
    setIsAgeRestrictionActive(false)
    setIsInCookieSettings(true)
  }

  const handleBackFromCookieSettings = () => {
    setIsInCookieSettings(false)
    setIsAgeRestrictionActive(true)
  }

  const handleSaveSettings = () => {
    if (!isEssentialCookiesEnabled || !isAgeCookiesEnabled) {
      setShowEssentialCookiesError(!isEssentialCookiesEnabled)
      setShowAgeCookiesError(!isAgeCookiesEnabled)
      return
    }

    const sentryManager = SentryManager.getInstance()
    sentryManager.updateConsent(isAnalyticalCookiesEnabled)

    CookieManager.initializeCookies({
      functional: isEssentialCookiesEnabled,
      analytics: isAnalyticalCookiesEnabled,
    })

    CookieManager.setAgeVerification()
    CookieManager.setAnalyticsCookie(isAnalyticalCookiesEnabled)
    onClose()
  }

  const handleConfirmAge = () => {
    const sentryManager = SentryManager.getInstance()
    sentryManager.updateConsent(true)

    CookieManager.setAgeVerification()
    CookieManager.initializeCookies({
      functional: true,
      analytics: true,
    })
    CookieManager.setAnalyticsCookie(true)
    setIsAgeRestrictionActive(false)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='medium'
      showCloseButton={false}
      disableOverlayClick={true}
    >
      <div
        className='p-4 bg-white rounded-lg shadow-lg text-left max-h-[80vh] overflow-y-auto
    scrollbar-thin scrollbar-thumb-anthracite/50 hover:scrollbar-thumb-anthracite 
    scrollbar-track-transparent 
    [&::-webkit-scrollbar]:w-2 
    [&::-webkit-scrollbar-track]:bg-transparent 
    [&::-webkit-scrollbar-thumb]:rounded-full'
      >
        <div className='flex items-center justify-center mb-6'>
          <img
            src='/images/logos/logo-sexagogo-redblack.png'
            className='max-h-20'
          />
        </div>

        {isAgeRestrictionActive && (
          <>
            <h2 className='text-xl md:text-2xl xl:text-3xl font-bold mb-4 text-center text-anthracite'>
              {t('welcome-message')}
            </h2>
            <p className=' text-center text-anthracite mb-8'>
              {t('age-restriction-message')}
            </p>
            <ul className='mb-8 space-y-4'>
              <li className='flex items-center'>
                <div className='flex-shrink-0'>
                  <AlertCircle className='w-6 h-6 text-green-500' />
                </div>
                <span className='ml-3 text-gray-700 flex-grow'>
                  {t('confirm-age')}
                </span>
              </li>
              <li className='flex items-center'>
                <div className='flex-shrink-0'>
                  <AlertCircle className='w-6 h-6 text-green-500' />
                </div>
                <span className='ml-3 text-gray-700 flex-grow'>
                  <Link
                    to='/cookies/'
                    className='text-anthracite hover:underline'
                  >
                    {t('consent-cookies')}
                  </Link>
                </span>
              </li>
              <li className='flex items-center'>
                <div className='flex-shrink-0'>
                  <AlertCircle className='w-6 h-6 text-green-500' />
                </div>
                <span className='ml-3 text-gray-700 flex-grow'>
                  <Link
                    to='/terms-of-use/'
                    className='text-anthracite hover:underline'
                  >
                    {t('accept-terms')}
                  </Link>
                </span>
              </li>
            </ul>
            <div className='text-center w-full '>
              <div className='flex w-full space-x-2 justify-center'>
                <MyButton variant='green' onClick={handleConfirmAge}>
                  <VscPass className='text-2xl text-white' />
                  {t('i-am-over-18')}
                </MyButton>
                <MyButton
                  variant='redpink-anthracite'
                  to='http://www.google.com'
                >
                  <VscCircleSlash className='text-2xl text-white' />
                  {t('i-am-not-18')}
                </MyButton>
              </div>
              <MyButton
                variant='anthracite'
                onClick={handleOpenCookieSettings}
                className='w-full mt-2'
              >
                {t('settings-navigation')}
              </MyButton>
            </div>
          </>
        )}

        {isInCookieSettings && (
          <>
            <button
              onClick={handleBackFromCookieSettings}
              className='text-white p-2 rounded-lg hover:bg-anthracite transition-all absolute left-2 top-2 bg-redpink'
            >
              <ArrowLeft className='w-6 h-6' />
            </button>
            <div className='flex items-center justify-center mb-6 relative'>
              <h2 className='text-xl font-bold text-center'>
                {t('navigation-settings')}
              </h2>
            </div>
            <div className='space-y-0'>
              <div className='flex items-center justify-between '>
                <MyCheckbox
                  label={t('i-am-over-18')}
                  checked={isAgeCookiesEnabled}
                  onChange={setIsAgeCookiesEnabled}
                  name='i-am-over-18'
                  className='w-full pr-2'
                  variant='default'
                />
                <Info
                  className='w-6 h-6 text-gray-500 cursor-pointer'
                  onClick={() => setOpenInfo(openInfo === 'age' ? null : 'age')}
                />
              </div>
              {openInfo === 'age' && (
                <div className='text-gray-600 text-sm mt-2'>
                  <p className=' pl-4 pr-6 border-l-2 border-redpink ml-6'>
                    {t('age-cookies-description')}
                  </p>
                </div>
              )}
              <div className='flex items-center justify-between'>
                <MyCheckbox
                  label={t('functional-cookies')}
                  checked={isEssentialCookiesEnabled}
                  className='w-full pr-2'
                  onChange={setIsEssentialCookiesEnabled}
                  name='functional-cookies'
                  variant='default'
                />
                <Info
                  className='w-6 h-6 text-gray-500 cursor-pointer'
                  onClick={() =>
                    setOpenInfo(openInfo === 'essential' ? null : 'essential')
                  }
                />
              </div>
              {openInfo === 'essential' && (
                <div className='text-gray-600 text-sm mt-2'>
                  <p className=' pl-4 pr-6 border-l-2 border-redpink ml-6'>
                    {t('cookies-functional-description')}
                  </p>
                  <table className='w-full mt-2 text-left border'>
                    <tbody>
                      {COOKIE_DESCRIPTIONS.ESSENTIAL.map(cookie => (
                        <tr key={cookie.key} className='border'>
                          <td className='p-2 w-1/5 border text-anthracite'>
                            {cookie.key}
                          </td>
                          <td className='p-2 w-4/5 border'>
                            {typeof t(cookie.descriptionKey) === 'string'
                              ? t(cookie.descriptionKey)
                              : ''}{' '}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className='flex items-center justify-between'>
                <MyCheckbox
                  label={t('analytical-cookies')}
                  checked={isAnalyticalCookiesEnabled}
                  className='w-full pr-2'
                  onChange={setIsAnalyticalCookiesEnabled}
                  name='analytical-cookies'
                  variant='default'
                />
                <Info
                  className='w-6 h-6 text-gray-500 cursor-pointer'
                  onClick={() =>
                    setOpenInfo(openInfo === 'analytical' ? null : 'analytical')
                  }
                />
              </div>
              {openInfo === 'analytical' && (
                <div className='text-gray-600 text-sm mt-2'>
                  <p className=' pl-4 pr-6 border-l-2 border-redpink ml-6'>
                    {t('cookies.sg-analytical-description')}
                  </p>
                  <table className='w-full mt-2 text-left border'>
                    <tbody>
                      {COOKIE_DESCRIPTIONS.ANALYTICS.map(cookie => (
                        <tr key={cookie.key} className='border'>
                          <td className='p-2 w-1/5 border text-anthracite'>
                            {cookie.key}
                          </td>
                          <td className='p-2 w-4/5 border'>
                            {typeof t(cookie.descriptionKey) === 'string'
                              ? t(cookie.descriptionKey)
                              : ''}{' '}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {showEssentialCookiesError && (
              <div className='bg-redlight border  text-redpink px-4 py-1 rounded text-sm relative mt-4'>
                <strong className='font-bold'>{t('error')}:</strong>{' '}
                {t('functional-cookies-required')}
              </div>
            )}
            {showAgeCookiesError && (
              <div className='bg-redlight border  text-redpink px-4 py-1 text-sm rounded relative mt-2'>
                <strong className='font-bold'>{t('error')}:</strong>{' '}
                {t('age-cookies-required')}
              </div>
            )}
            <div className='text-center mt-4 flex justify-between'>
              <MyButton
                variant='redpink-anthracite'
                onClick={handleSaveSettings}
              >
                {t('save-settings')}
              </MyButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default AgeRestrictionModal
